"use client";

import { useState } from "react";
import { useFansUnitedSDK } from "fansunited-data-layer/client";
import RichText from "@/components/RichText";
import { useLabels } from "@/lib/labels/LabelsProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { withdrawConsent, submitConsents, richTextToPlainText } from "@/features/profile-consent/profile-consent-utils";
import { useConsents } from "@/features/profile-consent/useConsents";
interface ConsentSettingsProps {
  /** Path to the delete-account anchor (already computed in parent). */
  basePath: string;
}

/**
 * Profile-page section for managing accepted profile consents.
 *
 * - Optional consents: toggle on/off (calls submitConsents / withdrawConsent).
 * - Required consents: read-only with a "withdraw via account deletion" notice.
 *
 * Hidden when the tenant has no consents configured.
 */
export default function ConsentSettings({
  basePath
}: ConsentSettingsProps) {
  const labels = useLabels();
  const sdk = useFansUnitedSDK();
  const {
    consents
  } = useTenantSettings();
  // Surface choice is arbitrary — the SDK call returns the user's full accepted set
  // regardless of the surface we ask the hook about.
  const {
    accepted,
    refresh
  } = useConsents("signup");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const t = (labels as {
    profileConsent?: Record<string, string>;
  }).profileConsent ?? {};
  if (consents.length === 0) return null;
  const optional = consents.filter(c => !c.required);
  const required = consents.filter(c_0 => c_0.required);
  const isAccepted = (id: string) => accepted?.has(id) ?? false;
  const toggleOptional = async (consentId: string, nextChecked: boolean) => {
    setBusyId(consentId);
    setError(null);
    try {
      if (nextChecked) {
        const def = consents.find(c_1 => c_1.consentId === consentId);
        await submitConsents(sdk, [{
          consentId,
          content: def ? richTextToPlainText(def.body) : null
        }]);
      } else {
        await withdrawConsent(sdk, consentId);
      }
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.loadFailed || "Something went wrong.");
    } finally {
      setBusyId(null);
    }
  };
  return <section className="space-y-4 rounded-lg border border-border bg-card p-6">
            <div>
                <h3 className="text-base font-semibold text-foreground">
                    {t.settingsTitle || "Consents & Privacy"}
                </h3>
            </div>

            {optional.length > 0 && <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                        {t.settingsDescriptionOptional || "Manage your optional preferences below."}
                    </p>
                    {optional.map(c_2 => <label key={c_2.consentId} className="flex items-start gap-3 cursor-pointer">
                            <input type="checkbox" className="mt-1 h-4 w-4 accent-primary cursor-pointer disabled:cursor-not-allowed" checked={isAccepted(c_2.consentId)} disabled={busyId === c_2.consentId} onChange={e => toggleOptional(c_2.consentId, e.target.checked)} />
                            <div className="text-sm text-foreground flex-1 [&_a]:text-primary [&_a]:underline">
                                <RichText content={c_2.body} />
                            </div>
                        </label>)}
                </div>}

            {required.length > 0 && <div className="space-y-3 pt-3 border-t border-border">
                    {required.map(c_3 => <div key={c_3.consentId} className="text-sm text-foreground/80">
                            <div className="[&_a]:text-primary [&_a]:underline">
                                <RichText content={c_3.body} />
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                                {t.settingsRequiredNotice || "This consent is required to maintain your account. To withdraw, delete your account."}{" "}
                                <a href={`${basePath}/profile#settings`} className="text-primary hover:underline">
                                    {t.deleteAccountLink || "Delete account"}
                                </a>
                                .
                            </p>
                        </div>)}
                </div>}

            {error && <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded px-3 py-2">{error}</div>}
        </section>;
}